/**
 * Five-cell vendor-status matrix: Tekion / Stock Type / Rapid Recon store
 * board / Mill Recon board / HomeNet. Each cell names its feed above the
 * ACTUAL value that feed reports ("Sold", "Special", "Parts Hold", "InPrep"),
 * on the bucket's pastel fill — the fact in words, the interpretation in
 * color, matching how the Excel workbook has always read. Stock Type is a
 * Tekion fact with no bucket of its own (a Special unit is the rental/loaner
 * fleet — Ian's JRAC25000 catch), so it sits on the neutral fill. The tooltip
 * carries the translation (`Tekion: "Sold" — Gone`) so the bucket word is one
 * hover away and color is never the only accessible carrier. Visual spec: the
 * approved audit-remodel mockup artifact `.vmatrix`/`.vcell`; colors via
 * THEME.problemDisplay only.
 */
import React from "react"

import { THEME } from "../../config/theme"

interface Props {
  tkBucket: string | null
  rrBucket: string | null
  hnBucket: string | null
  millBucket: string | null
  /** Raw feed values shown in the cells (bucket only colors them). */
  tkRaw: string | null
  rrRaw: string | null
  millRaw: string | null
  hnRaw: string | null
  /** Tekion stock type (New / Used / Special) — shown as its own cell. */
  stockType: string | null
}

const BUCKET_CELLS = THEME.problemDisplay.bucketCells as Record<
  string,
  { background: string; label: string }
>

function cellFor(bucket: string | null): { background: string; label: string } {
  if (!bucket) return { background: THEME.colors.lightGray, label: "—" }
  return BUCKET_CELLS[bucket] ?? { background: THEME.colors.lightGray, label: bucket }
}

export default function VendorMatrix({
  tkBucket,
  rrBucket,
  hnBucket,
  millBucket,
  tkRaw,
  rrRaw,
  millRaw,
  hnRaw,
  stockType,
}: Props) {
  // Cell order per Ian: Stock Type leads (what kind of unit is this?), then
  // the feeds in pipeline order — Tekion, store recon, Mill, website.
  const cells: Array<{ key: string; name: string; bucket: string | null; raw: string | null; isType?: boolean }> = [
    { key: "type", name: "Stock Type", bucket: null, raw: stockType, isType: true },
    { key: "tk", name: "Tekion", bucket: tkBucket, raw: tkRaw },
    { key: "rr", name: "Rapid Recon", bucket: rrBucket, raw: rrRaw },
    { key: "mill", name: "Mill Recon", bucket: millBucket, raw: millRaw },
    { key: "hn", name: "HomeNet", bucket: hnBucket, raw: hnRaw },
  ]

  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {cells.map(({ key, name, bucket, raw, isType }) => {
        const cell = cellFor(bucket)
        // The pipeline spells absence two ways: a literal "No Record"
        // placeholder on merged rows, null on rows that skipped the merge
        // (e.g. resurrected zombie twins). Render both as the bucket's own
        // word so identical facts never show differently.
        let rawText = (raw ?? "").trim()
        if (rawText.toLowerCase() === "no record") rawText = ""
        const shown = rawText || (bucket ? cell.label : "—")
        // Stock type comes from Tekion on inventory rows, but from the site
        // listing itself on website rows (a ghost has no Tekion record to
        // ask) — the label stays neutral about its source.
        const description = isType
          ? `Stock type: ${rawText || "—"}`
          : rawText
            ? `${name}: "${rawText}" — ${cell.label}`
            : `${name}: ${cell.label}`
        return (
          <span
            key={key}
            role="img"
            aria-label={description}
            title={description}
            style={{
              minWidth: 76,
              maxWidth: 120,
              textAlign: "center",
              padding: "3px 8px 4px",
              borderRadius: 3,
              background: cell.background,
              whiteSpace: "nowrap",
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                fontSize: 8.5,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: THEME.problemDisplay.bucketCellFeedLabel,
              }}
            >
              {name}
            </span>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: THEME.problemDisplay.bucketCellText,
                display: "block",
                maxWidth: 104,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {shown}
            </span>
          </span>
        )
      })}
    </span>
  )
}
